import { OrderAddress, OrderItem, Transaction, TransactionEvent } from '../types/checkout';
import { common, instabank } from './upstream';
import { TransactionOrGap } from './transactions/types';
import { formatPhoneNumber } from '../helpers/formatters';
import { isGap, Gap } from '../helpers/paging';
import { clone } from 'lodash/fp';
import { captureItems } from '../orders/captureOrder/actions';
import { PaymentProductType } from './types';
import differenceInDays from 'date-fns/differenceInDays';

export const getCurrentEvent = (transaction: Transaction): TransactionEvent => {
    if (transaction.events) {
        const idx = transaction.events.length - 1;
        if (idx >= 0) {
            const event = transaction.events[idx];
            if (event) {
                return event;
            }
        }
    }
    return {
        transaction_status: 'UNKNOWN',
        created_at: new Date().toISOString(),
    };
};

const getSignificantEvents = (transaction: Transaction): TransactionEvent[] => {
    const events: TransactionEvent[] = transaction.events || [];
    const lastCorrectionIdx = events.reduce(
        (acc: number, event: TransactionEvent, i: number) => (event.correction ? i : acc),
        -1
    );
    if (lastCorrectionIdx >= 0) {
        return events.filter((event: TransactionEvent, i: number) => i >= lastCorrectionIdx);
    }
    return transaction.events;
};

export const hasCorrection = (transaction: Transaction): boolean => {
    const events: TransactionEvent[] = transaction.events || [];
    const correctionEvent = events.find((event) => event.correction);
    return correctionEvent !== undefined;
};

export const hasNegativeLineItem = (transaction: Transaction): boolean => {
    if (transaction.items) {
        return transaction.items.reduce<boolean>((acc, i) => acc || (i.amount || 0) < 0, false);
    }
    return false;
};

export const hasLineIds = (transaction: Transaction): boolean => {
    if (transaction.items) {
        return transaction.items.reduce<boolean>((acc, item) => acc && item.line_id !== undefined, true);
    }
    return false;
};

export const previousOperationsHasItems = (transaction: Transaction): boolean => {
    const operationTypes = ['CAPTURE', 'REFUND'];
    if (transaction) {
        const capturesAndRefunds = (transaction.events as TransactionEvent[]).filter((event) =>
            operationTypes.includes(event.event || ''));
        return capturesAndRefunds.reduce<boolean>(
            (acc, event) =>
                acc &&
                event.items !== undefined &&
                event.items.length > 0 &&
                event.items.reduce<boolean>((bdd, item) => bdd && item.line_id !== undefined, true),
            true
        );
    }
    return false;
};

const getQuantityByLineId = (event: TransactionEvent) => {
    return (event.items || []).reduce<{[lineId: string]: number}>((acc, item) => ({
        ...acc,
        [item.line_id || '']: (item.quantity || 0) + (acc[item.line_id || ''] || 0),
    }), {});
};

const getAmountByLineId = (event: TransactionEvent) => {
    return (event.items || []).reduce<{[lineId: string]: number}>((acc, item) => ({
        ...acc,
        [item.line_id || '']: (item.amount || 0) + (acc[item.line_id || ''] || 0),
    }), {});
};

export const previousOperationsAreConsistent = (transaction: Transaction): boolean => {

    // check if the capture and operations are consistent with what has been authorized
    // const operationTypes = ['AUTHORIZE', 'CAPTURE', 'REFUND'];
    try {
        if (transaction) {
            const authorize = (transaction.events as TransactionEvent[]).find((event) =>
                event.event === 'AUTHORIZE' && event.success);
            if (!authorize) {
                throw new Error('No authorize event found');
            }
            const authorizedAmounts = getAmountByLineId(authorize);
            const authorizedQuantities = getQuantityByLineId(authorize);

            const capturedAmounts: {[lineId: string]: number} = {};
            const capturedQuantities: {[lineId: string]: number} = {};

            const operationTypes = ['CAPTURE', 'REFUND'];
            const capturesAndRefunds = (transaction.events as TransactionEvent[]).filter((event) =>
                operationTypes.includes(event.event as string) && event.success);

            capturesAndRefunds.forEach((event) => {
                if (event.event === 'CAPTURE') {
                    // check that we have authorized items and amounts
                    (event.items || []).forEach((captureItem) => {
                        if (captureItem.line_id) {
                            // add to the captured amounts
                            capturedAmounts[captureItem.line_id] = (captureItem.amount || 0) + (capturedAmounts[captureItem.line_id] || 0);
                            capturedQuantities[captureItem.line_id] = (captureItem.quantity || 0) + (capturedQuantities[captureItem.line_id] || 0);

                            // subtract from the authorized amounts
                            authorizedAmounts[captureItem.line_id] = (authorizedAmounts[captureItem.line_id] || 0) - (captureItem.amount || 0);
                            if (authorizedAmounts[captureItem.line_id] < 0) {
                                throw new Error(`Authorized amount is less than captured amount for line ${captureItem.line_id}`);
                            }
                            authorizedQuantities[captureItem.line_id] = (authorizedQuantities[captureItem.line_id] || 0) - (captureItem.quantity || 0);
                            if (authorizedQuantities[captureItem.line_id] < 0) {
                                throw new Error(`Authorized quantity is less than captured for line ${captureItem.line_id}`);
                            }
                        }
                    });
                }
                if (event.event === 'REFUND') {
                    // check that we have already captured items and amounts
                    (event.items || []).forEach((refundItem) => {
                        if (refundItem.line_id) {
                            // subtract from the captured amounts
                            capturedAmounts[refundItem.line_id] = (capturedAmounts[refundItem.line_id] || 0) - (refundItem.amount || 0);
                            if (capturedAmounts[refundItem.line_id] < 0) {
                                throw new Error(`Authorized amount is less than captured amount for line ${refundItem.line_id}`);
                            }
                            capturedQuantities[refundItem.line_id] = (capturedQuantities[refundItem.line_id] || 0) - (refundItem.quantity || 0);
                            if (capturedQuantities[refundItem.line_id] < 0) {
                                throw new Error(`Authorized quantity is less than captured for line ${refundItem.line_id}`);
                            }
                        }
                    });
                }
            });
            return true;
        }
    } catch (e) {
        console.log(e);
    }
    return false;
};


const startsAtCorrection = (events: TransactionEvent[]) => {
    if (events.length) {
        return events[0].correction ? events[0].correction : undefined;
    }
    return undefined;
};

const getReducerInitialCapturableAmount = (transaction: Transaction, significantEvents: TransactionEvent[]) => {
    const correction = startsAtCorrection(significantEvents);
    if (correction) {
        return correction.remaining_capture_amount;
    }
    return transaction.amount;
};

export const getCapturableAmount = (transaction: Transaction): number => {
    const events = getSignificantEvents(transaction);
    return events.reduce((acc: number, event: TransactionEvent) => {
        if (acc === 0) {
            return 0;
        }
        if (
            ['AUTHORIZATION_VOIDED', 'SETTLEMENT_COMPLETED', 'DECLINED', 'CAPTURED', 'UNKNOWN'].includes(
                event.transaction_status || ''
            )
        ) {
            return 0;
        }
        if (event.event === 'CAPTURE' && event.success) {
            return acc - (event.amount || 0);
        }
        if (event.event === 'VOID' && event.success) {
            return 0;
        }
        return acc;
    }, getReducerInitialCapturableAmount(transaction, events));
};

export const getVoidedAmount = (transaction: Transaction): number => {
    const events = getSignificantEvents(transaction);
    return events.reduce((acc: number, event: TransactionEvent) => {
        if (event.event === 'VOID' && event.success) {
            return acc + (event.amount || 0);
        }

        return acc;
    }, 0);
};

export const getCapturedAmount = (transaction: Transaction): number => {
    if (['AUTHORIZATION_VOIDED', 'DECLINED', 'UNKNOWN'].includes(transaction.status || '')) {
        return 0;
    }
    return transaction.amount - getCapturableAmount(transaction) - getVoidedAmount(transaction);
};

const sumCapturedVat = (events: TransactionEvent[]) => {
    return events.reduce((sum: number, event: TransactionEvent) => {
        if (event.event === 'CAPTURE' && event.success && event.calculated_vat_amount) {
            return sum + event.calculated_vat_amount;
        }
        return sum;
    }, 0);
};

export const getCapturedVatAmount = (transaction: Transaction): number => {
    return sumCapturedVat(transaction.events);
};

export const getRefundedAmount = (transaction: Transaction): number => {
    if (['AUTHORIZATION_VOIDED', 'DECLINED', 'UNKNOWN', 'CAPTURED'].includes(transaction.status || '')) {
        return 0;
    }
    return getCapturedAmount(transaction) + getVoidedAmount(transaction) - getRefundableAmount(transaction);
};

export const hasCapturableState = (transaction: Transaction): boolean => {
    // Route to upstream implementation
    if (transaction.payment_product === 'instabank') {
        return instabank.hasCapturableState(transaction);
    }
    return common.hasCapturableState(transaction);
};

export const hasRefundableState = (transaction: Transaction): boolean => {
    return ['CAPTURED', 'PARTIALLY_CAPTURED', 'PARTIALLY_REFUNDED', 'PARTIALLY_CAPTURED_REFUNDED'].includes(
        transaction.status || ''
    );
};

export const hasRefundablePaymentProvider = (transaction: Transaction): boolean => {
    return !['santander.debit_account'].includes(transaction.payment_product_type);
};

export const hasVoidableState = (transaction: Transaction): boolean => {
    if (transaction.payment_product_type === 'dintero.zero') {
        return transaction.status === 'AUTHORIZED';
    }
    if (transaction.amount === 0) {
        return false;
    }
    if (getCapturableAmount(transaction) === transaction.amount) {
        return true;
    }
    // Only some payment types can void the rest if some of the amount is captured
    const whitelistedPaymentTypes = [
        'payex.creditcard',
        'payex.vipps',
        'payex.mobilepay',
        'vipps',
        'klarna.klarna',
        'klarna.billie',
    ];
    if (!whitelistedPaymentTypes.includes(transaction.payment_product_type)) {
        return false;
    }
    const capturable = getCapturableAmount(transaction);
    return capturable > 0;
};

const getRefundableAmountBasis = (transaction: Transaction, significantEvents: TransactionEvent[]) => {
    const correction = startsAtCorrection(significantEvents);
    if (correction) {
        const postCorrectionCaptured = significantEvents.reduce((acc: number, event: TransactionEvent) => {
            // we need to skip the correction event here since we are using the basis from the correction
            if (event.event === 'CAPTURE' && event.success && event.correction === undefined) {
                return acc + (event.amount || 0);
            }
            return acc;
        }, 0);
        return correction.remaining_refund_amount + postCorrectionCaptured;
    }
    return transaction.amount - getCapturableAmount(transaction);
};

export const getRefundableAmount = (transaction: Transaction): number => {
    const events = getSignificantEvents(transaction);
    const canRefund = events.reduce((acc, event) => {
        return acc && !['AUTHORIZATION_VOIDED', 'DECLINED', 'UNKNOWN'].includes(event.transaction_status || '');
    }, true);
    if (canRefund) {
        const alreadyRefundedAmount = events.reduce((acc: number, event: TransactionEvent) => {
            if (event.event === 'REFUND' && event.success) {
                return acc + (event.amount || 0);
            }
            return acc;
        }, 0);
        return getRefundableAmountBasis(transaction, events) - alreadyRefundedAmount;
    }
    return 0;
};

const customerName = (address: OrderAddress) => {
    const names = [address.first_name, address.last_name];
    const fullName = names.filter((x) => x).join(' ');
    return fullName || (address.phone_number && formatPhoneNumber(address.phone_number)) || address.email || '';
};

export const getCustomerName = (transaction: Transaction) => {
    const billingName = transaction.billing_address && customerName(transaction.billing_address);
    const shippingName = transaction.shipping_address && customerName(transaction.shipping_address);
    return (
        billingName ||
        shippingName ||
        (transaction.customer?.phone_number && formatPhoneNumber(transaction.customer?.phone_number)) ||
        transaction.customer?.email ||
        transaction.customer?.customer_id ||
        ''
    );
};

export const getProductName = (transaction: Transaction) => {
    if (['instabank', 'collector', 'santander', 'swish'].includes(transaction.payment_product)) {
        return `payments.product_types.${transaction.payment_product_type}`;
    }
    if (transaction.payment_product_type === 'netaxept.creditcard') {
        if (transaction.card && transaction.card.brand) {
            return transaction.card.brand;
        }
        return `payments.product_types.${transaction.payment_product_type}`;
    }

    if (transaction.payment_product === 'bambora') {
        if (transaction.payment_product_type === 'bambora.creditcard' && transaction.card && transaction.card.brand) {
            const brand = transaction.card.brand.toLowerCase();
            if (brand.indexOf('visa') > -1) {
                return 'Visa';
            }
            if (brand.indexOf('mastercard') > -1) {
                return 'MasterCard';
            }
            return transaction.card.brand;
        }
        return `payments.product_types.${transaction.payment_product_type}`;
    }

    if (transaction.payment_product === 'payex') {
        if (transaction.payment_product_type === 'payex.creditcard' && transaction.card && transaction.card.brand) {
            return transaction.card.brand;
        }
        return `payments.product_types.${transaction.payment_product_type}`;
    }
    if (transaction.payment_product === 'vipps') {
        return `payments.product_types.vipps`;
    }
    return [`payments.product_types.${transaction.payment_product_type}`, transaction.payment_product_type];
};

export const getNextPageArgs = (transactions: TransactionOrGap[]): { [key: string]: string } => {
    if (transactions.length === 0) {
        return {};
    }
    const last = transactions[transactions.length - 1];
    return { starting_after: last.id || '' };
};

export const getGapPageArgs = (transactions: TransactionOrGap[], id: string): { [key: string]: string } => {
    const gap = transactions.find((t) => isGap(t) && t.id === id);
    if (!gap) {
        return {};
    }
    return { starting_after: gap.id || '' };
};

export const isTransaction = (obj: TransactionOrGap): obj is Transaction => {
    return (obj as Gap).gap !== true;
};

export const hasSplitPayment = (transaction: Transaction): boolean => {
    const itemWithSplits = (transaction.items || []).find(item => item.splits);
    return !!itemWithSplits;
};

export const usesPaymentType = (transaction: Transaction, paymentType: PaymentProductType): boolean => {
    return transaction.payment_product_type === paymentType;
};

export const canExtendAuthorization = (transaction: Transaction): boolean => {
    const isKlarna = usesPaymentType(transaction, PaymentProductType.Klarna); 
    const isBillie = usesPaymentType(transaction, PaymentProductType.Billie); 
    if (!(isKlarna || isBillie)) {
        return false;
    }

    const successfulAuthorizationEvents =
    transaction.payment_product === 'klarna'
        ? transaction.events.filter(
            (event: TransactionEvent) =>
                event.event === 'AUTHORIZE' && event.success
        )
        : [];

    const oldestAuthorizationEvent = successfulAuthorizationEvents.length > 0
        ? successfulAuthorizationEvents[0]
        : undefined;

    const newestAuthorizationEvent = successfulAuthorizationEvents.length > 0
        ? successfulAuthorizationEvents[successfulAuthorizationEvents.length - 1]
        : undefined;


    const reachedExtendMaxLimit =
      oldestAuthorizationEvent &&
      hasReachedExtendMaxLimit(oldestAuthorizationEvent);

    const authorizedMoreThanTwoWeeksAgo = wasAuthorizedMoreThanTwoWeeksAgo(newestAuthorizationEvent);

    return (
        hasCapturableState(transaction) &&
        getCapturableAmount(transaction) > 0 &&
        !reachedExtendMaxLimit &&
        authorizedMoreThanTwoWeeksAgo
    );
};

const hasReachedExtendMaxLimit = (event: TransactionEvent): boolean => {
    // The max limit for extending the authorization date on an order
    // is 180 days from the original authorization date.
    if (event.created_at) {
        return new Date().getTime() - new Date(event.created_at).getTime() > 180 * 24 * 60 * 60 * 1000;
    }

    return false;
};

const wasAuthorizedMoreThanTwoWeeksAgo = (event: TransactionEvent | undefined): boolean => {
    // The extend authorization button should only be shown if the authorization is older than 14 days.
    if (event && event.created_at) {
        const now = new Date();
        const authorizationDate = new Date(event.created_at);
        return differenceInDays(now, authorizationDate) >= 14;
    }
    return true;
};

export const formatExpirationDate = (date: Date): string => {
    return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
};
